// HomeStats.js — Landing page impact stats bar.
// Reuses the same CMS-backed /api/blog/about-stats/ endpoint and default
// figures already published on the About page, so numbers stay consistent
// across the site instead of introducing a second, divergent set of claims.
import React, { useEffect, useRef, useState } from 'react';
// LazyMotion + domAnimation instead of the full `motion` namespace — this
// component only ever does simple initial/animate/transition tweens (no
// drag, layout animation, or exit animations), which is exactly what
// domAnimation covers, at roughly half the bundled size of importing
// `motion` directly. Both HomeStats and HowItWorks are required to stay in
// the homepage's initial bundle, so this is the one place in the app where
// framer-motion's bundle size is actually worth trimming.
import { LazyMotion, domAnimation, m, useInView } from 'framer-motion';
import CountUp from 'react-countup';
import { getAboutStats } from '../services/api';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import './HomeStats.css';

const DEFAULT_STATS = [
  { value: 20, suffix: '+', label: 'Students Placed', color: '#3498db' },
  { value: 50, suffix: '+', label: 'Partner Universities', color: '#8b5cf6' },
];

const HomeStats = () => {
  const [stats, setStats] = useState(DEFAULT_STATS);
  const sectionRef = useRef(null);
  // framer-motion v10's useInView takes `amount`, not `threshold` — the
  // latter is silently ignored, which is why this previously fired at the
  // library's default (amount: "some", i.e. any pixel) rather than 30%.
  const inView = useInView(sectionRef, { once: true, amount: 0.3 });
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    getAboutStats()
      .then((list) => {
        if (Array.isArray(list) && list.length > 0) {
          setStats(list.map((s) => ({
            value: Number(s.value) || 0,
            suffix: s.suffix || '',
            prefix: s.prefix || '',
            label: s.label || '',
            color: s.color || '#3498db',
          })));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section ref={sectionRef} className="home-stats-section">
      <LazyMotion features={domAnimation} strict>
        <div className="home-stats-container">
          {stats.map((stat, index) => (
            // Opacity is never part of this animation — the card is always
            // fully visible (to crawlers, print, screenshot tools, and
            // anyone who never scrolls it into view), only its position
            // animates in. Reduced-motion skips that transform too, so the
            // card simply appears at rest with no motion at all.
            <m.div
              key={index}
              initial={{ y: reducedMotion ? 0 : 24 }}
              animate={{ y: reducedMotion || inView ? 0 : 24 }}
              transition={reducedMotion ? { duration: 0 } : { duration: 0.5, delay: index * 0.1 }}
              className="home-stat-card"
            >
              <div className="home-stat-dot" style={{ backgroundColor: `${stat.color}20` }}>
                <span style={{ color: stat.color }}>●</span>
              </div>
              <div className="home-stat-value">
                {reducedMotion ? (
                  `${stat.prefix || ''}${stat.value.toLocaleString()}${stat.suffix}`
                ) : (
                  <CountUp
                    start={0}
                    end={stat.value}
                    duration={2.2}
                    prefix={stat.prefix || ''}
                    suffix={stat.suffix}
                    separator=","
                  />
                )}
              </div>
              <div className="home-stat-label">{stat.label}</div>
            </m.div>
          ))}
        </div>
      </LazyMotion>
    </section>
  );
};

export default HomeStats;
